import React, { ReactElement, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { KycSection } from '@/pages/settings/verify//kyc-sections/KycSection';
import GenericFilesUpload from '@/pages/settings/verify/GenericFilesUpload';
import { BtnOneLineBlue } from '@/components/buttons/BtnOneLineBlue';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { selectUserId } from '@/store/selectors/userSelectors';
import { kycGetDocuments } from '@/store/actions/kycActions';
import BffApiService from '@/shared/bffApiService';
import { resizeImage } from '@/utils/utils';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { KycType, VerificationStatus } from '@lucky7ventures/bff-types';

export interface DropzoneMeta {
  id: string;
  text?: string;
  errorMessageTranslationKey: string;
  minFiles: number;
  maxFiles: number;
}

export interface DocumentSectionMetadata {
  type: KycType;
  status: VerificationStatus;
  title: string;
  acceptedFileTypes: string[];
  dropzonesMeta: DropzoneMeta[];
  // This error message is triggered on submit and validates the number of files uploaded
  validationErrorMessage: string;
  // Supporting documents don't have description section for now
  description?: ReactElement;
}

const KycDocument = ({
  type,
  status,
  title,
  description,
  acceptedFileTypes,
  dropzonesMeta,
  validationErrorMessage,
}: DocumentSectionMetadata) => {
  const [files, setFiles] = useState<{ [key: string]: File } | null>(null);
  const [error, setError] = useState('');
  const [resizingLoading, setResizingLoading] = useState(false);
  const userId = useSelector(selectUserId);
  const { request, loading } = useBffApiRequest<null>();
  const intl = useIntl();
  const dispatch = useDispatch();

  const handleOnFilesDrop = (acceptedFiles: { id: string; files: File[] }) => {
    const { id, files } = acceptedFiles;
    setFiles(state => ({ ...state, [id]: files[0] }));
  };

  const handleOnSubmit = async () => {
    const minNumberOfFilesNeeded = dropzonesMeta.reduce((total, dropzone) => {
      total += dropzone.minFiles;
      return total;
    }, 0);

    const maxNumberOfFilesNeeded = dropzonesMeta.reduce((total, dropzone) => {
      total += dropzone.maxFiles;
      return total;
    }, 0);

    if (
      files === null ||
      Object.keys(files).length < minNumberOfFilesNeeded ||
      Object.keys(files).length > maxNumberOfFilesNeeded
    ) {
      setError(validationErrorMessage);
      return;
    }
    setError('');

    // Resize files
    setResizingLoading(true);
    const resizedFilesPromises: Promise<{ id: string; file: File }>[] = Object.keys(files).map(
      async id => {
        const file = files[id];
        if (['image/png', 'image/jpeg'].includes(file.type)) {
          try {
            const resizedFile = await resizeImage(file, 2000, 4000);
            return { id, file: resizedFile };
          } catch (e) {
            // do nothing, return original file
          }
        }
        return { id, file };
      },
    );
    setResizingLoading(false);

    const resizedFiles = await Promise.all(resizedFilesPromises);
    const formData = new FormData();
    resizedFiles.forEach(({ id, file }) => {
      formData.set(id, file);
    });

    const handleOnSuccess = () => {
      dispatch(kycGetDocuments());
      triggerGTag(GTagEvents.kyc_upload_success, { userId });
      window.scrollTo(0, 0);
    };

    const handleOnError = () => {
      setError(intl.formatMessage({ id: 'error.44' }));
    };

    request({
      apiMethod: BffApiService.uploadKycDocuments,
      payload: formData,
      pathParams: {
        kycType: type,
      },
      axiosConfig: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      successCallback: handleOnSuccess,
      errorCallback: handleOnError,
    });
  };

  return (
    <KycSection status={status} title={title}>
      <div className="flex flex-col gap-6 [&_button]:min-h-fit">
        {description}
        {dropzonesMeta.map(({ id, text, errorMessageTranslationKey }, index) => (
          <div className="flex flex-col items-center gap-4 w-full" key={index}>
            {text && <p className="m-0 self-start">{text}</p>}
            <GenericFilesUpload
              id={id}
              onFilesDrop={handleOnFilesDrop}
              errorMessageTranslationKey={errorMessageTranslationKey}
              acceptedFileTypes={acceptedFileTypes}
              disabled={loading}
            />
          </div>
        ))}
        <div className="flex flex-col gap-2">
          {error && <span className="text-red-light font-bold text-sm">{error}</span>}
          <BtnOneLineBlue
            onClickHandler={handleOnSubmit}
            text={intl.formatMessage({ id: 'settings.verify.uploadButton' })}
            isLoading={loading || resizingLoading}
          />
        </div>
      </div>
    </KycSection>
  );
};

export default KycDocument;
